import { createBrowserRouter } from 'react-router-dom';
import { AppLayout } from '@/app/app-layout';
import { CatalogChrome } from '@/app/catalog-chrome';
import { lazyPage } from '@/lib/stale-chunk';
// Direct import, not the feature barrel: this is a plain constant list, and
// pulling it through the barrel would eagerly load the admin pages the lazy
// imports below exist to defer.
import { ADMIN_PERMISSIONS } from '@/features/admin/admin-tabs';
import { AcceptInvitationPage } from '@/features/auth/accept-invitation-page';
import { CallbackPage } from '@/features/auth/callback-page';
import { LoginPage } from '@/features/auth/login-page';
import { NotFoundPage, RouteError } from './error-page';
import { ProtectedRoute } from './protected-route';
import { RequirePermission } from './require-permission';

// In-shell feature pages are lazy — the initial (login) load stays small; each
// section's chunk is fetched on first navigation (Suspense fallback in AppLayout).
const DashboardPage = lazyPage(() => import('@/features/dashboard/dashboard-page').then((m) => ({ default: m.DashboardPage })));
const AwardsListPage = lazyPage(() => import('@/features/awards').then((m) => ({ default: m.AwardsListPage })));
const AwardBuilderPage = lazyPage(() => import('@/features/awards').then((m) => ({ default: m.AwardBuilderPage })));
const TemplatesPage = lazyPage(() => import('@/features/awards').then((m) => ({ default: m.TemplatesPage })));
const PreAssessmentsPage = lazyPage(() => import('@/features/pre-assessments').then((m) => ({ default: m.PreAssessmentsPage })));
const PreAssessmentEditPage = lazyPage(() => import('@/features/pre-assessments').then((m) => ({ default: m.PreAssessmentEditPage })));
const CatalogPage = lazyPage(() => import('@/features/catalog').then((m) => ({ default: m.CatalogPage })));
const OpportunityDetailPage = lazyPage(() => import('@/features/catalog').then((m) => ({ default: m.OpportunityDetailPage })));
const MyApplicationsPage = lazyPage(() => import('@/features/applications').then((m) => ({ default: m.MyApplicationsPage })));
const ProfilePage = lazyPage(() => import('@/features/profile').then((m) => ({ default: m.ProfilePage })));
const ApplicationWorkspacePage = lazyPage(() => import('@/features/applications').then((m) => ({ default: m.ApplicationWorkspacePage })));
const ReviewQueuePage = lazyPage(() => import('@/features/review').then((m) => ({ default: m.ReviewQueuePage })));
const PipelinePage = lazyPage(() => import('@/features/stages').then((m) => ({ default: m.PipelinePage })));
const WinnersPage = lazyPage(() => import('@/features/stages').then((m) => ({ default: m.WinnersPage })));
const MyEvaluationsPage = lazyPage(() => import('@/features/evaluations').then((m) => ({ default: m.MyEvaluationsPage })));
// From its own file, not the barrel: the panel page is a chunk of its own.
const Panel360Page = lazyPage(() =>
  import('@/features/evaluations/pages/panel-360-page').then((m) => ({ default: m.Panel360Page })),
);
const ApprovalsPage = lazyPage(() => import('@/features/approvals').then((m) => ({ default: m.ApprovalsPage })));
const FulfilmentPage = lazyPage(() => import('@/features/fulfilment').then((m) => ({ default: m.FulfilmentPage })));
const ReviewDetailPage = lazyPage(() => import('@/features/review').then((m) => ({ default: m.ReviewDetailPage })));
const TeamPage = lazyPage(() => import('@/features/team').then((m) => ({ default: m.TeamPage })));
const AdminLayout = lazyPage(() => import('@/features/admin').then((m) => ({ default: m.AdminLayout })));
const AdminIndexRedirect = lazyPage(() => import('@/features/admin').then((m) => ({ default: m.AdminIndexRedirect })));
const TenantsPage = lazyPage(() => import('@/features/admin').then((m) => ({ default: m.TenantsPage })));
const UsersPage = lazyPage(() => import('@/features/admin').then((m) => ({ default: m.UsersPage })));
const RolesPage = lazyPage(() => import('@/features/admin').then((m) => ({ default: m.RolesPage })));
const PlatformPage = lazyPage(() => import('@/features/admin').then((m) => ({ default: m.PlatformPage })));
const PartnersPage = lazyPage(() => import('@/features/admin').then((m) => ({ default: m.PartnersPage })));
// Named for the collision: `TemplatesPage` above is the AWARD template
// library. This one is the platform's message templates.
const MessageTemplatesPage = lazyPage(() => import('@/features/admin').then((m) => ({ default: m.TemplatesPage })));

/**
 * Route tree. `/login` + `/auth/callback` are public and eager; everything else
 * lives inside the authenticated shell behind <ProtectedRoute>, with per-route
 * <RequirePermission> defence-in-depth over the permission-filtered nav.
 * `errorElement` keeps a thrown route from white-screening.
 */
export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage />, errorElement: <RouteError /> },
  { path: '/auth/callback', element: <CallbackPage /> },
  // Public by necessity: the invitee has no account until this page succeeds.
  { path: '/invitations/accept', element: <AcceptInvitationPage />, errorElement: <RouteError /> },
  {
    // The opportunities catalogue — no session required. Public chrome for a
    // logged-out visitor, the app shell for anyone signed in, on one URL so a
    // shared opportunity link opens for both.
    path: '/opportunities',
    element: <CatalogChrome />,
    errorElement: <RouteError />,
    children: [
      { index: true, element: <CatalogPage /> },
      // Accepts a slug (hanga-pitch-2025) or an id — the API resolves either, so
      // links shared before slugs existed keep working.
      { path: ':slug', element: <OpportunityDetailPage /> },
    ],
  },
  {
    // Authenticated applicant area, in the SAME shell as the staff surface: an
    // applicant gets the navigation rail with their applications and profile in
    // it, rather than a thin public header that made the signed-in area feel
    // like a different product. No permission required — applicants hold none,
    // and ownership is enforced object-level by each endpoint.
    path: '/my',
    element: (
      // APPLICANT audience: an unauthenticated visitor here is an applicant, and
      // must not be handed to the staff sign-in surface.
      <ProtectedRoute audience="APPLICANT">
        <AppLayout />
      </ProtectedRoute>
    ),
    errorElement: <RouteError />,
    children: [
      { path: 'applications', element: <MyApplicationsPage /> },
      { path: 'applications/:id', element: <ApplicationWorkspacePage /> },
      // The applicant's own profile. No permission: applicants hold none, and
      // ownership is enforced object-level by the endpoint.
      { path: 'profile', element: <ProfilePage /> },
    ],
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <AppLayout />
      </ProtectedRoute>
    ),
    errorElement: <RouteError />,
    children: [
      { index: true, element: <DashboardPage /> },
      { path: 'awards', element: <RequirePermission permission="awards.create"><AwardsListPage /></RequirePermission> },
      // Listed before ':id' for readability; the router ranks the static segment
      // above the dynamic one either way, so /awards/templates never resolves as
      // an award id. `awards.view` matches the endpoint that lists templates;
      // approving inside is gated separately, so a viewer sees the library
      // without the action.
      { path: 'awards/templates', element: <RequirePermission permission="awards.view"><TemplatesPage /></RequirePermission> },
      { path: 'pre-assessments', element: <RequirePermission permission="awards.update"><PreAssessmentsPage /></RequirePermission> },
      { path: 'pre-assessments/new', element: <RequirePermission permission="awards.update"><PreAssessmentEditPage /></RequirePermission> },
      { path: 'pre-assessments/:id', element: <RequirePermission permission="awards.update"><PreAssessmentEditPage /></RequirePermission> },
      // Official placements. Listed before `:id` so /awards/:id/winners is not
      // swallowed as an award id. `applications.view_all` matches GET /winners;
      // assigning inside is gated on shortlist.decide, so a viewer sees the
      // podium without the board.
      // One panel's progress (#41, Panel 360). Listed before `:id` with the other
      // award sub-pages; any-of because staff who follow judging and the panel's
      // own chair (a judge, `scores.submit`) both read it. The page picks staff or
      // chair mode and the server decides per panel, so this gate is defence in
      // depth only — `awards/:id` would answer them with the builder's 403.
      {
        path: 'awards/:awardId/rounds/:stageId/panels/:panelId',
        element: (
          <RequirePermission permission={['evaluators.view', 'evaluators.assign', 'scores.submit']}>
            <Panel360Page />
          </RequirePermission>
        ),
      },
      { path: 'awards/:id/winners', element: <RequirePermission permission="applications.view_all"><WinnersPage /></RequirePermission> },
      { path: 'awards/:id', element: <RequirePermission permission="awards.create"><AwardBuilderPage /></RequirePermission> },
      // The staff review queue. `applications.view_all` matches the endpoint that
      // lists them; the screening actions inside are gated separately, so a
      // viewer sees the queue without the decisions.
      { path: 'applications', element: <RequirePermission permission="applications.view_all"><ReviewQueuePage /></RequirePermission> },
      // One submission in full. Same permission as the queue — the backend now
      // enforces it per AWARD on the read itself, so a narrower grant gets a 403
      // here rather than someone else's answers.
      { path: 'applications/:id', element: <RequirePermission permission="applications.view_all"><ReviewDetailPage /></RequirePermission> },
      // Running an award, as distinct from configuring one. `applications.view_all`
      // matches the endpoint that lists the stages; every ACTION inside is gated
      // on its own permission (stages.manage, scores.aggregate, shortlist.decide),
      // so a viewer sees the pipeline's state without being offered its levers.
      { path: 'pipeline', element: <RequirePermission permission="applications.view_all"><PipelinePage /></RequirePermission> },
      // `scores.submit` is the evaluator capability the whole surface needs;
      // object-level scope is the assignment's own evaluatorId, enforced by the
      // server, so a judge only ever sees their own list.
      { path: 'evaluations', element: <RequirePermission permission="scores.submit"><MyEvaluationsPage /></RequirePermission> },
      // The approver's own queue and delegations. Both endpoints require
      // `approvals.decide`; gate configuration lives on Award progress behind
      // `approvals.configure`, so a tenant admin who can set gates is not an
      // approver and must not land here.
      { path: 'approvals', element: <RequirePermission permission="approvals.decide"><ApprovalsPage /></RequirePermission> },
      // What is still owed to the people who won. `fulfilment.view` is the
      // permission the queue endpoint itself is guarded by; advancing a tracker
      // and recording payment/delivery need `fulfilment.update`, so a viewer
      // sees the outstanding work without being offered its levers.
      { path: 'fulfilment', element: <RequirePermission permission="fulfilment.view"><FulfilmentPage /></RequirePermission> },
      // The tenant's own people. `staff.view` is the permission the directory
      // endpoint itself is guarded by; invite/suspend/role controls inside are
      // gated separately, so a viewer sees the team without the actions.
      { path: 'team', element: <RequirePermission permission="staff.view"><TeamPage /></RequirePermission> },
      {
        // The Super Admin console. The parent guard is any-of (does this person
        // belong here at all); each child re-asserts the permission that actually
        // guards its endpoints, so a narrower holder gets 403 on the section, not
        // a blank screen — and the tab strip never offers it in the first place.
        path: 'admin',
        element: (
          <RequirePermission permission={ADMIN_PERMISSIONS}>
            <AdminLayout />
          </RequirePermission>
        ),
        children: [
          { index: true, element: <AdminIndexRedirect /> },
          { path: 'tenants', element: <RequirePermission permission="settings.platform"><TenantsPage /></RequirePermission> },
          { path: 'users', element: <RequirePermission permission="users.manage"><UsersPage /></RequirePermission> },
          { path: 'roles', element: <RequirePermission permission="roles.manage"><RolesPage /></RequirePermission> },
          { path: 'templates', element: <RequirePermission permission="settings.platform"><MessageTemplatesPage /></RequirePermission> },
          { path: 'platform', element: <RequirePermission permission="settings.platform"><PlatformPage /></RequirePermission> },
          { path: 'integrations', element: <RequirePermission permission="integrations.manage"><PartnersPage /></RequirePermission> },
        ],
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
